import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {AuthenticatedBaseComponent} from '../../../authenticated-base.component';
import {Backend, PERMISSION} from '@generated-backend-types';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ContentHeaderService} from '../../../../services/content-header.service';
import {TitleService} from '../../../../services/title.service';
import {ActivatedRoute, Router} from '@angular/router';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {FormGroupComponent} from '../../../../components/form-inputs/form-group/form-group.component';
import {of, switchMap, takeUntil, throwError} from 'rxjs';
import {EditModeEnum} from '../../../../enums/EditModeEnum';
import {
  DateTimeSelectorComponent
} from '../../../../components/form-inputs/date-time-selector/date-time-selector.component';
import {UserSessionService} from '../../../../services/user-session.service';
import {PublicationService} from '../../../../services/api/administration/publication.service';
import {ToastrService} from 'ngx-toastr';
import {NotEnoughPermissionsError} from '../../../../errors/NotEnoughPermissionsError';
import {HttpErrorResponse} from '@angular/common/http';
import {FormErrorService} from '../../../../services/form-error.service';
import {StateCardComponent} from '../../../../components/state-card/state-card.component';
import {ShowComponent as AuthorShowComponent} from './authors/show/show.component';
import {CreateComponent as AuthorCreateComponent} from './authors/create/create.component';
import {DeleteCardComponent} from '../../../../components/form-inputs/delete-card/delete-card.component';
import {IfHasAllPermissionsDirective} from '../../../../directives/if-has-all-permissions.directive';
import {
  MultiSelectWithServiceListComponent
} from '../../../../components/form-inputs/multi-select-with-service-list/multi-select-with-service-list.component';
import Publication = Backend.App.Resources.Dashboard.Administration.Publication;
import PublicationData = Backend.App.Data.Dashboard.Administration.PublicationData;
import ModelStateEnum = Backend.App.Enums.ModelStateEnum;
import {DiagnosisService} from '../../../../services/api/diagnosis.service';

@Component({
  selector: 'app-authenticated-administration-publications-edit',
  templateUrl: './edit.component.html',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormGroupComponent,
    DateTimeSelectorComponent,
    StateCardComponent,
    AuthorShowComponent,
    AuthorCreateComponent,
    DeleteCardComponent,
    IfHasAllPermissionsDirective,
    MultiSelectWithServiceListComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditComponent extends AuthenticatedBaseComponent implements OnInit {
  public publication?: Publication;
  public mode = EditModeEnum.CREATE;

  public isAddingAuthor = false;

  public form: StrictFormGroup<PublicationData> = this.fb.group({
    state: this.fb.control(ModelStateEnum.DRAFT, [Validators.required]),
    title: this.fb.control('', [Validators.required]),
    journal: this.fb.control('', [Validators.required]),
    published_at: this.fb.control('', [Validators.required]),
    link_text: this.fb.control('', [Validators.required]),
    link_destination: this.fb.control('', [Validators.required]),
    abstract: this.fb.control(''),
    diagnoses: this.fb.control([]),
  });

  public readonlyFormValues: StrictFormGroup<{ slug: string }> = this.fb.group({
    slug: this.fb.control({
      value: this.translate.instant('authenticated.administration.publications.edit.form.slug.empty'),
      disabled: true
    }),
  })
  protected readonly ModelStateEnum = ModelStateEnum;
  protected readonly PERMISSION = PERMISSION;

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly fb: NonNullableFormBuilder,
    private readonly userSessionService: UserSessionService,
    private readonly publicationService: PublicationService,
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly formErrorService: FormErrorService,
    protected readonly diagnosisService: DiagnosisService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public ngOnInit() {
    this.activatedRoute.data.pipe(takeUntil(this.onDestroy$)).subscribe(data => {
      const publication: Publication | undefined = data['publication'];

      if (publication) {
        this.publication = publication;

        this.form.patchValue({
          ...publication,
          diagnoses: publication.diagnoses?.map(diagnosis => diagnosis.id) ?? [],
        });

        this.mode = EditModeEnum.EDIT;
        this.cdr.markForCheck();

        this.readonlyFormValues.controls.slug.setValue(publication.slug);
      }

      this.titleService.setTitle('authenticated.administration.publications.' + this.mode + '.title');
    });
  }

  public save() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (!this.form.valid) {
      return;
    }

    const data = this.form.value as PublicationData;

    of(this.publication?.id).pipe(
      switchMap(id => {
        if (id) {
          if (!this.userSessionService.hasAllPermission([PERMISSION.ADMINISTRATION_PUBLICATION_UPDATE_ALL])) {
            throwError(() => new NotEnoughPermissionsError());
          }

          return this.publicationService.update(id, data);
        }

        if (!this.userSessionService.hasAllPermission([PERMISSION.ADMINISTRATION_PUBLICATION_CREATE_ALL])) {
          throwError(() => new NotEnoughPermissionsError());
        }

        return this.publicationService.create(data);
      })
    ).subscribe({
      next: res => {
        this.form.markAsPristine();
        this.form.markAsUntouched();

        this.toastrService.success(this.translate.instant(`authenticated.administration.publications.${this.mode}.success`));
        this.publication = res.data;

        if (this.mode === EditModeEnum.CREATE) {
          this.router.navigateByUrl(`/administration/publications/${this.publication.id}`).then();
        }

        this.mode = this.publication?.id ? EditModeEnum.EDIT : EditModeEnum.CREATE;
        this.cdr.markForCheck();
      },
      error: err => {
        if (err instanceof NotEnoughPermissionsError) {
          this.toastrService.error(this.translate.instant('misc.not_enough_permissions_error'));
        } else if (err instanceof HttpErrorResponse) {
          this.form.markAllAsTouched();
          this.form.updateValueAndValidity({onlySelf: false, emitEvent: true});
          this.formErrorService.mapErrorToForm(this.form, err);
        } else {
          this.toastrService.error(this.translate.instant(`authenticated.administration.publications.${this.mode}.error`));
        }
      },
    })

    this.cdr.markForCheck();
  }

  public reloadAuthors() {
    if (this.publication) {
      this.publicationService.get(this.publication.id, true).subscribe({
        next: res => {
          this.publication = res.data;
          this.cdr.markForCheck();
        }
      })
    }
  }

  public onAuthorCreated() {
    this.isAddingAuthor = false;
    this.reloadAuthors();
  }

  public deleteItem() {
    if (!this.userSessionService.hasAllPermission([PERMISSION.ADMINISTRATION_PUBLICATION_DELETE_ALL])) {
      this.toastrService.error(this.translate.instant('misc.not_enough_permissions_error'));
    }

    if (this.publication) {
      this.publicationService.delete(this.publication.id).subscribe({
        next: () => {
          this.toastrService.success(this.translate.instant('authenticated.administration.publications.delete.success'));
          this.router.navigateByUrl('/administration/publications').then();
        },
        error: () => {
          this.toastrService.error(this.translate.instant('authenticated.administration.publications.delete.error'));
        }
      });
    }
  }
}
